import { Fragment, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Upload, Sparkles, Plus, Trash2, GripVertical, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useBook } from "@/context/BookContext";
import { Page, Header, Field, Sel, Spin } from "@/components/common";
import { Switch } from "@/components/ui/switch";

const AGES = ["3 – 5 yaş", "6 – 8 yaş", "8 – 12 yaş", "9 – 11 yaş", "12+ yaş"];
const SIZES = ["A4 (210 × 297 mm)", "A5 (148 × 210 mm)", "Kare (210 × 210 mm)", "16 × 24 cm"];
const LANGS = ["Türkçe", "İngilizce", "Almanca"];
const COVER = "https://static.prod-images.emergentagent.com/jobs/0c8a379b-c978-4bc5-b007-00b9c9c84fe2/images/435135c0653e53c52904f6c353d147111425d4a5612cd99f6d1673ff63c80690.jpeg";

export default function NewBook() {
  const nav = useNavigate();
  const { setBookId, loadBooks } = useBook();
  const fileRef = useRef();
  const [f, setF] = useState({ title: "", author: "ZOR Yayın Ekibi", age_group: "8 – 12 yaş", page_size: SIZES[0], language: "Türkçe" });
  const [mode, setMode] = useState("text");
  const [chapters, setChapters] = useState([{ title: "", summary: "" }]);
  const [text, setText] = useState("");
  const [expand, setExpand] = useState(false);
  const [autoIll, setAutoIll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const setCh = (i, k, v) => setChapters((c) => c.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/books/parse-file", fd);
      setText(data.text); setFileName(data.filename);
      toast.success(`${data.filename} yüklendi (${data.text.length} karakter). Bölüm ve sayfa ayrımı oluşturulduktan sonra kontrol edin.`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Kitap başlığı zorunlu");
    if (mode === "summary" && !text.trim() && !chapters.some((c) => c.title || c.summary)) return toast.error("Özetten oluşturmak için bir özet veya bölüm özeti girin");
    setBusy(true);
    try {
      const { data } = await api.post("/books", { ...f, author: f.author || "ZOR Yayın Ekibi", chapters: chapters.filter((c) => c.title || c.summary), source_text: text, mode, expand_summary: expand, cover_url: "" });
      await loadBooks();
      setBookId(data.id);
      toast.success(mode === "summary" ? "Kitap oluşturuldu. AI hikâyeyi yazıyor; sayfalar 1-2 dakika içinde eklenecek." : `Kitap oluşturuldu: ${data.chapters.length} bölüm, ${data.page_count} sayfa. Lütfen ayrımı kontrol edin.`);
      nav(autoIll ? "/sayfa-gorseli?auto=1" : `/kitaplar/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const TabBtn = ({ id, icon: I, label }) => (
    <button onClick={() => setMode(id)} data-testid={`newbook-tab-${id}`} className={`flex-1 py-3 flex items-center justify-center gap-2 font-serif-d text-lg border-b-2 transition-colors ${mode === id ? "border-teal bg-[#e9f4f6] text-navy rounded-t-lg" : "border-transparent text-[#5f6f84]"}`}><I size={19} /> {label}</button>
  );

  return (
    <Page crumbs={["Yeni Kitap Oluştur"]}>
      <Header title="Yeni Kitap Oluştur" sub="Kitabınızın temel bilgilerini girin, içeriğini ekleyin ve üretim sürecini başlatın." />
      <div className="grid xl:grid-cols-[1fr_390px] gap-5">
        <div className="space-y-5">
          <div className="card-z p-5 grid md:grid-cols-3 gap-4">
            <Field label="Kitap başlığı *"><input className="inp" value={f.title} onChange={set("title")} placeholder="Gizli Bahçenin Anahtarı" data-testid="newbook-title" /></Field>
            <Field label="Yazar *"><input className="inp" value={f.author} onChange={set("author")} data-testid="newbook-author" /></Field>
            <Field label="Yaş grubu *"><Sel value={f.age_group} onChange={set("age_group")} options={AGES} testid="newbook-age" /></Field>
            <Field label="Sayfa boyutu *"><Sel value={f.page_size} onChange={set("page_size")} options={SIZES} testid="newbook-size" /></Field>
            <Field label="Dil *"><Sel value={f.language} onChange={set("language")} options={LANGS} testid="newbook-lang" /></Field>
          </div>
          <div className="card-z p-5">
            <div className="flex border-b mb-5"><TabBtn id="text" icon={FileText} label="Metin gir" /><TabBtn id="file" icon={Upload} label="Dosya yükle" /><TabBtn id="summary" icon={Sparkles} label="Özetten oluştur" /></div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif-d text-2xl font-semibold text-navy">Bölüm planı</h3>
              <button onClick={() => setChapters([...chapters, { title: "", summary: "" }])} className="btn-outline-teal px-4 py-2 flex items-center gap-2 text-sm" data-testid="add-chapter-btn"><Plus size={16} /> Bölüm ekle</button>
            </div>
            <div className="space-y-3 mb-5">
              {chapters.map((c, i) => (
                <div key={i} className="border rounded-lg p-3 flex items-center gap-3" data-testid={`chapter-row-${i}`}>
                  <GripVertical size={16} className="text-[#a3aebb]" />
                  <span className="w-10 h-10 rounded-full bg-[#e3f1f5] text-navy grid place-items-center font-serif-d text-lg">{i + 1}</span>
                  <input className="inp max-w-[240px]" placeholder="Bölüm adı" value={c.title} onChange={(e) => setCh(i, "title", e.target.value)} data-testid={`chapter-title-${i}`} />
                  <textarea className="inp flex-1 !py-2" rows={2} placeholder="Bölüm özeti" value={c.summary} onChange={(e) => setCh(i, "summary", e.target.value)} data-testid={`chapter-summary-${i}`} />
                  <button onClick={() => setChapters(chapters.filter((_, j) => j !== i))} className="text-[#d1453b]" data-testid={`chapter-delete-${i}`}><Trash2 size={17} /></button>
                </div>
              ))}
            </div>
            {mode === "file" && (
              <div onClick={() => fileRef.current.click()} className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-[#f5fafb] mb-4" data-testid="file-dropzone">
                <Upload className="mx-auto text-teal mb-2" /><div className="text-navy font-medium">{fileName || "Hikâye dosyanızı seçin (.txt, .docx, .pdf)"}</div>
                <div className="text-xs text-[#8a96a8]">"1. Bölüm" başlıkları otomatik bölüm olarak ayrılır, paragraflar sayfalara bölünür.</div>
                <input ref={fileRef} type="file" accept=".txt,.docx,.pdf,.md" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="file-input" />
              </div>
            )}
            <Field label={mode === "summary" ? "Kitap özeti (AI hikâyeye dönüştürür)" : "Kaynak metin *"}>
              <textarea className="inp min-h-[110px]" maxLength={100000} value={text} onChange={(e) => setText(e.target.value)} data-testid="newbook-source-text"
                placeholder={mode === "summary" ? "Örn: On yaşındaki Ada, dedesinden kalan anahtarla gizli bir bahçe keşfeder..." : "Kitabınızın metnini buraya yazın. Bölüm başlıkları, sahneler ve diyaloglar dahil tüm içeriği girebilirsiniz..."} />
            </Field>
            <div className="text-right text-xs text-[#6b7a8c] mt-1">{text.length.toLocaleString("tr-TR")} / 100.000</div>
            {mode !== "summary" && (
              <div className="flex items-start gap-3 mt-2">
                <Switch checked={expand} onCheckedChange={setExpand} data-testid="expand-summary-switch" />
                <div><div className="font-serif-d text-navy">Özeti genişlet</div><div className="text-xs text-[#6b7a8c]">Girilen metne göre daha detaylı bir kitap özeti oluşturulsun.</div></div>
              </div>
            )}
            <div className="flex items-start gap-3 mt-3">
              <Switch checked={autoIll} onCheckedChange={setAutoIll} data-testid="auto-illustrate-switch" />
              <div><div className="font-serif-d text-navy">Oluşturunca hemen resimle</div><div className="text-xs text-[#6b7a8c]">Karakterler metinden çıkarılır, onayınızdan sonra tüm sayfalar ve kapak tek seferde çizilir. Onaylamadan Emergent bakiyenizden görsel ücreti düşmez.</div></div>
            </div>
          </div>
        </div>
        <div className="card-z p-6 h-fit sticky top-20">
          <h3 className="font-serif-d text-2xl font-semibold text-navy">Kitap önizlemesi</h3>
          <p className="text-sm text-[#6b7a8c] mb-5">Bu bilgilerle oluşturulacak kitabın ön izlemesi.</p>
          <div className="relative mx-auto w-[260px] aspect-[2/3] rounded-md overflow-hidden shadow-[8px_10px_25px_rgba(0,0,0,.25)]">
            <img src={COVER} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-5 inset-x-0 text-center font-playfair text-white text-3xl px-4 leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,.7)]">{f.title || "Kitap Başlığı"}</div>
            <div className="absolute bottom-4 inset-x-0 text-center text-white font-serif-d drop-shadow">{f.author}</div>
          </div>
          <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-sm mt-6">
            {[["Başlık", f.title], ["Yazar", f.author], ["Yaş grubu", f.age_group], ["Sayfa boyutu", f.page_size], ["Dil", f.language]].map(([k, v]) => (<Fragment key={k}><dt className="text-[#6b7a8c]">{k}</dt><dd className="text-navy">{v || "—"}</dd></Fragment>))}
          </dl>
          <button onClick={submit} disabled={busy} className="btn-teal w-full py-3.5 mt-6 flex items-center justify-center gap-2 text-lg font-serif-d" data-testid="create-book-submit">{busy ? <><Spin /> Oluşturuluyor…</> : <><BookOpen size={20} /> Kitap oluştur</>}</button>
        </div>
      </div>
    </Page>
  );
}
